import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { Minus, Plus, Trash2, ShoppingBasket, ArrowRight, Truck } from 'lucide-react';

import {
  formatRupees,
  formatRupeesCompact,
  lineTotal,
  deliveryFeeFor,
  FREE_DELIVERY_THRESHOLD_PAISE,
  UNIT_LABELS,
  type UnitOfMeasure,
} from '@sreerasa/shared';
import { Button } from '@/components/ui/button';
import { useCart } from '@/features/cart/cart-store';
import { useAuth } from '@/features/auth/auth-context';
import { useProducts } from '@/lib/queries';
import { AnimatedNumber, transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/cart')({
  component: CartPage,
});

function CartPage() {
  const navigate = useNavigate();
  const { lines, setQuantity, remove } = useCart();
  const { isAuthenticated } = useAuth();

  // Prices are read live from the catalog and joined onto the cart lines —
  // the cart itself stores only IDs and quantities. A price that lived in the
  // cart would be a price we'd have to honour weeks later.
  const { data: products, isLoading } = useProducts({});
  const byId = new Map(products?.map((p) => [p.id, p]));

  const priced = lines.map((line) => {
    const product = byId.get(line.product_id);
    return {
      ...line,
      product,
      total: product ? lineTotal(product.price_paise, line.quantity) : 0,
    };
  });

  const subtotal = priced.reduce((sum, item) => sum + item.total, 0);
  const delivery = deliveryFeeFor(subtotal);
  const total = subtotal + delivery;
  const awayFromFree = FREE_DELIVERY_THRESHOLD_PAISE - subtotal;

  if (lines.length === 0) {
    return (
      <div className="mx-auto max-w-lg px-4 py-28 text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={transitions.spring}
          className="mx-auto flex size-20 items-center justify-center rounded-full bg-soil-100"
        >
          <ShoppingBasket className="size-9 text-soil-400" aria-hidden />
        </motion.div>
        <h1 className="mt-6 font-display text-2xl font-bold text-soil-900">Your basket is empty</h1>
        <p className="mt-2 text-soil-600">
          Nothing in here yet. The mangoes are not going to add themselves.
        </p>
        <Link to="/shop" className="mt-8 inline-block">
          <Button size="lg" className="group">
            Browse the shop
            <ArrowRight className="transition-transform group-hover:translate-x-1" aria-hidden />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-display text-display font-black text-soil-900">Your basket</h1>
      <p className="mt-2 text-soil-600">
        {lines.length} {lines.length === 1 ? 'item' : 'items'} from{' '}
        {new Set(priced.map((p) => p.product?.farm_name).filter(Boolean)).size || 1} farm
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_20rem]">
        {/* ── Lines ── */}
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {priced.map((item) => (
              <motion.div
                key={item.product_id}
                layout
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0 }}
                transition={transitions.base}
                className="flex gap-4 rounded-card border border-soil-200 bg-white p-4"
              >
                <div className="size-20 shrink-0 overflow-hidden rounded-xl bg-soil-100">
                  {item.image && <img src={item.image} alt="" className="size-full object-cover" />}
                </div>

                <div className="min-w-0 flex-1">
                  {item.product ? (
                    <Link
                      to="/shop/$slug"
                      params={{ slug: item.product.slug }}
                      className="font-display font-bold text-soil-900 hover:text-farm-700"
                    >
                      {item.title}
                    </Link>
                  ) : (
                    <p className="font-display font-bold text-soil-900">{item.title}</p>
                  )}

                  {item.product?.village && (
                    <p className="mt-0.5 text-xs text-soil-500">
                      {item.product.village}, {item.product.district}
                    </p>
                  )}

                  {/* A product can vanish between adding and checkout — sold
                      out, delisted, moderated away. Say so plainly rather than
                      letting checkout fail with a cryptic error. */}
                  {!item.product && !isLoading && (
                    <p className="mt-1 text-xs font-medium text-danger">
                      No longer available — remove it to check out.
                    </p>
                  )}

                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-1 rounded-lg border border-soil-200 p-0.5">
                      <button
                        type="button"
                        onClick={() => setQuantity(item.product_id, item.quantity - 1)}
                        aria-label={`Reduce ${item.title}`}
                        className="flex size-7 items-center justify-center rounded-md text-soil-600 hover:bg-soil-100"
                      >
                        <Minus className="size-3.5" aria-hidden />
                      </button>
                      <span className="w-12 text-center text-sm font-semibold tabular-nums">
                        {item.quantity} {UNIT_LABELS[item.unit as UnitOfMeasure] ?? item.unit}
                      </span>
                      <button
                        type="button"
                        onClick={() => setQuantity(item.product_id, item.quantity + 1)}
                        disabled={Boolean(item.product && item.quantity >= item.product.stock_qty)}
                        aria-label={`Add more ${item.title}`}
                        className="flex size-7 items-center justify-center rounded-md text-soil-600 hover:bg-soil-100 disabled:opacity-30"
                      >
                        <Plus className="size-3.5" aria-hidden />
                      </button>
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="font-display font-bold text-soil-900">
                        {formatRupees(item.total)}
                      </span>
                      <button
                        type="button"
                        onClick={() => remove(item.product_id)}
                        aria-label={`Remove ${item.title}`}
                        className="flex size-8 items-center justify-center rounded-lg text-soil-400 transition-colors hover:bg-red-50 hover:text-danger"
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* ── Summary ── */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-card border border-soil-200 bg-white p-5">
            <h2 className="font-display text-lg font-bold text-soil-900">Summary</h2>

            <dl className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-soil-600">Subtotal</dt>
                <dd className="font-medium tabular-nums">{formatRupees(subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-soil-600">Delivery</dt>
                <dd className="font-medium tabular-nums">
                  {delivery === 0 ? (
                    <span className="text-farm-600">Free</span>
                  ) : (
                    formatRupees(delivery)
                  )}
                </dd>
              </div>
            </dl>

            {awayFromFree > 0 && (
              <div className="mt-4 rounded-xl bg-harvest-100 p-3">
                <p className="flex items-center gap-2 text-xs font-medium text-harvest-800">
                  <Truck className="size-3.5 shrink-0" aria-hidden />
                  Add {formatRupeesCompact(awayFromFree)} more for free delivery
                </p>
                <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-harvest-200">
                  <motion.div
                    className="h-full rounded-full bg-harvest-500"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.min(100, (subtotal / FREE_DELIVERY_THRESHOLD_PAISE) * 100)}%`,
                    }}
                    transition={transitions.slow}
                  />
                </div>
              </div>
            )}

            <div className="mt-4 flex items-baseline justify-between border-t border-soil-100 pt-4">
              <span className="font-display font-bold text-soil-900">Total</span>
              <AnimatedNumber
                value={formatRupees(total)}
                className="font-display text-2xl font-black text-soil-900"
              />
            </div>

            <Button
              size="lg"
              full
              className="mt-5"
              onClick={() =>
                // Bounce through sign-in with a redirect, rather than showing
                // a checkout form that will fail at submit.
                navigate({
                  to: isAuthenticated ? '/checkout' : '/auth/sign-in',
                  search: isAuthenticated ? undefined : { redirect: '/checkout' },
                })
              }
            >
              {isAuthenticated ? 'Checkout' : 'Sign in to check out'}
              <ArrowRight aria-hidden />
            </Button>

            <p className="mt-3 text-center text-xs text-soil-500">
              Pay cash or UPI when it arrives. Nothing upfront.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
